import { useEffect, useRef, useState } from "react";

import useDebouncedCallback from "@mapmycustomers/shared/util/hook/useDebouncedCallback";

import usePlacePredictionsGetter from "@app/component/input/Address/utils/usePlacePredictionsGetter";
import GlobalSearchItemType from "@app/enum/GlobalSearchItemType";
import { AddressGlobalSearchItem } from "@app/types/globalSearch/GlobalSearchItem";

// Stable while disabled: the modal recomputes its sections whenever this array changes
const NO_ITEMS: AddressGlobalSearchItem[] = [];

// Google's own SDK, not our API, so no saga wraps it: the address pickers call it from a hook too
const useAddressPredictions = (
  query: string,
  enabled: boolean
): { answered: boolean; items: AddressGlobalSearchItem[] } => {
  const [getPredictions] = usePlacePredictionsGetter();
  const [answer, setAnswer] = useState<{ items: AddressGlobalSearchItem[]; text?: string }>({
    items: NO_ITEMS,
  });
  const queryRef = useRef(query);

  const fetchPredictions = useDebouncedCallback([
    async (text: string) => {
      const predictions = await getPredictions(text);
      if (text !== queryRef.current) {
        return;
      }
      setAnswer({
        items: predictions.map(({ description, place_id, structured_formatting }) => ({
          entity: undefined,
          entityType: undefined,
          prediction: { description, place_id, structured_formatting },
          title: "",
          type: GlobalSearchItemType.ADDRESS,
        })),
        text,
      });
    },
    500,
  ]);

  useEffect(() => {
    queryRef.current = query;
    if (enabled && query.trim().length >= 3) {
      fetchPredictions(query);
    } else {
      fetchPredictions.cancel();
      setAnswer({ items: NO_ITEMS });
    }
  }, [enabled, fetchPredictions, query]);

  return enabled
    ? { answered: answer.text === query, items: answer.items }
    : { answered: false, items: NO_ITEMS };
};

export default useAddressPredictions;
